'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { automationId } from '@terra/shared-utils';
import { Box, token } from '@terra/ui-styled-system';
import { Search01Icon } from '@terra/ui-icons';
import { IconButton } from '../../components/icon-button';
import { Input } from '../../components/input';
import { Tooltip } from '../../components/tooltip';

export type ListPageSearchProps = {
  value: string;
  onValueChange: (value: string) => void;
  placeholder: string;
  width?: string;
  dataTestid?: string;
};

export function ListPageSearch({
  value,
  onValueChange,
  placeholder,
  width = '240px',
  dataTestid = '',
}: ListPageSearchProps) {
  const t = useTranslations('designSystem.listPage');
  const [isExpanded, setIsExpanded] = useState(false);

  if (!isExpanded && value === '') {
    return (
      <Tooltip title={t('search')}>
        <IconButton
          size="2xs"
          variant="ghost"
          aria-label={t('search')}
          onClick={() => setIsExpanded(true)}
          {...automationId(dataTestid, 'open')}
        >
          <Search01Icon size={14} />
        </IconButton>
      </Tooltip>
    );
  }

  return (
    <Box position="relative" w={width} flexShrink={0}>
      <Search01Icon
        size={14}
        color={token('colors.fg.neutral.subtle')}
        style={{
          position: 'absolute',
          left: '8px',
          top: '50%',
          transform: 'translateY(-50%)',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      />
      <Input
        autoFocus
        size="xs"
        h="6"
        pl="7"
        value={value}
        placeholder={placeholder}
        aria-label={placeholder}
        autoComplete="off"
        onChange={(event) => onValueChange(event.target.value)}
        onBlur={() => setIsExpanded(false)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            onValueChange('');
            setIsExpanded(false);
          }
        }}
        {...automationId(dataTestid, 'input')}
      />
    </Box>
  );
}
